import PropTypes from 'prop-types';
import { theme } from '_constants/theme';
import styled from 'styled-components';
import Step from '_components/atomic/Step';
import Icon from '_components/atomic/Icon';
import { ICONS_ALIASES } from '_constants/icons';
import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import SwiperCore, { Navigation } from 'swiper';
import { useSwiperRef } from '_hooks';

const { colors, devices, fontSizes, spaces } = theme;
SwiperCore.use([Navigation]);

const StyledThreeStepSection = styled.div`
  max-width: ${spaces.large24X * 10 + 80}px;
  width: 100%;
  margin-top: ${spaces.large24X}px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  .headerSection {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 500;
    font-size: ${fontSizes.large11X}px;
    line-height: 130%;
    text-align: center;
    color: ${colors.gray[800]};
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
  }
  .subHeaderSection {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 400;
    font-size: ${fontSizes.large}px;
    line-height: 150%;
    margin-top: ${spaces.medium}px;
    text-align: center;
    color: ${colors.gray[700]};
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
  }
  .columnDesktopContainer {
    display: flex;
    margin-top: ${spaces.large7X - 2}px;
    justify-content: center;
    gap: 13px;
  }
  .columnMobileContainer {
    display: none;
  }
  @media ${devices.tablet} {
    margin-top: ${spaces.large9X}px;
    margin-bottom: ${spaces.large9X}px;
    margin-left: 0;
    margin-right: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0px;
    flex: none;
    order: 1;
    flex-grow: 0;
    .headerSection {
      padding: 0px ${spaces.regular}px;
      font-size: ${fontSizes.large2X}px;
      line-height: 150%;
    }
    .subHeaderSection {
      padding: 0px ${spaces.regular}px;
      font-size: ${fontSizes.regular}px;
      line-height: 150%;
      margin-top: ${spaces.smallX}px;
      margin-bottom: ${spaces.large2X}px;
    }
    .columnDesktopContainer {
      display: none;
    }
    .columnMobileContainer {
      display: block;
      width: 100%;
      padding-left: ${spaces.regular}px;
      padding-right: ${spaces.regular}px;
    }
    .swiper-slide {
      width: 90%;
    }
  }
`;
const StyledStep = styled.div`
  width: 30%;

  img {
    width: 180px;
  }

  .container {
    height: 100%;

    .description {
      margin-top: ${spaces.small - 2}px;
    }
  }

  @media ${devices.laptopM} {
    img {
      width: ${spaces.large24X}px;
    }
  }

  @media ${devices.tablet} {
    width: initial;
    img {
      width: 180px;
    }
  }
`;
const StyledDivider = styled.div`
  align-items: center;
  display: flex;
`;
const StyledSliderNavigation = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 34px;
  padding-right: ${spaces.regular}px;
  .navButton {
    cursor: pointer;
  }
  .swiper-button-disabled {
    cursor: initial;
    svg {
      path {
        fill: ${colors.gray[500]};
      }
    }
  }
`;
const ThreeStepSection = ({ title, subTitle, steps, elRef }) => {
  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();
  return (
    <StyledThreeStepSection ref={elRef}>
      <div className="headerSection">{title}</div>
      {subTitle && <div className="subHeaderSection">{subTitle}</div>}
      <div className="columnDesktopContainer">
        {steps.map((step, idx) => {
          const { header, description, icon } = step;
          return (
            <React.Fragment key={`step:${idx}`}>
              <StyledStep>
                <Step
                  top={`STEP 0${idx + 1}`}
                  bottom={
                    <div className="container">
                      <div className="header">{header}</div>
                      <div className="description">{description}</div>
                    </div>
                  }
                  imgSrc={icon}
                />
              </StyledStep>
              {steps?.length !== idx + 1 && (
                <StyledDivider>
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={theme.colors.blue[500]}
                  />
                </StyledDivider>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="columnMobileContainer">
        <Swiper
          spaceBetween={16}
          slidesPerView={'auto'}
          navigation={{
            prevEl,
            nextEl,
          }}
        >
          {steps.map((step, idx) => {
            const { header, description, icon } = step;
            return (
              <SwiperSlide key={`step:${idx}`}>
                <StyledStep>
                  <Step
                    top={`STEP 0${idx + 1}`}
                    bottom={
                      <div className="container">
                        <div className="header">{header}</div>
                        <div className="description">{description}</div>
                      </div>
                    }
                    imgSrc={icon}
                  />
                </StyledStep>
              </SwiperSlide>
            );
          })}
          <StyledSliderNavigation>
            <div ref={prevElRef} className="navButton">
              <Icon
                alias={ICONS_ALIASES.arrowLeft}
                color={colors.blue[500]}
                height={20}
                width={20}
              />
            </div>
            <div ref={nextElRef} className="navButton">
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={colors.blue[500]}
                height={20}
                width={20}
              />
            </div>
          </StyledSliderNavigation>
        </Swiper>
      </div>
    </StyledThreeStepSection>
  );
};

ThreeStepSection.propTypes = {
  title: PropTypes.node,
  subTitle: PropTypes.node,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.node,
      description: PropTypes.node,
      icon: PropTypes.string,
    })
  ),
};

export default ThreeStepSection;
